import { AgGridTable } from 'components/AgGrid/AgGridTable';
import type { BimWorkOrderPriceValue, WorkOrderStatusEnum } from 'gql/graphql';
import { Loader } from 'uikit';

import type { ColDef, ColGroupDef, GridReadyEvent, RowClassRules } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { ForwardedRef } from 'react';
import { useCallback } from 'react';

import { useBimWorkOrderTotalCommonColumns } from './hooks';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
    cellClass: 'ag-right-aligned-cell',
};

const tableLoadingComponent = () => <Loader />;

type IBimWorkOrderTotalsTable<TData> = {
    tableData: TData[] | undefined | null;
    workOrderStatus: WorkOrderStatusEnum;
    hasInProgress: boolean;
    isFrameContract: boolean;
    tableColumns?: (ColDef<TData> | ColGroupDef<TData>)[];
    rowClassRules?: RowClassRules<TData>;
    tableRef?: ForwardedRef<AgGridReact<TData>>;
};

export const BimWorkOrderTotalsTable = <
    T extends Partial<{
        completedValue?: BimWorkOrderPriceValue;
        remainderValue?: BimWorkOrderPriceValue;
    }>,
>({
    workOrderStatus,
    hasInProgress,
    isFrameContract,
    tableData,
    tableColumns,
    rowClassRules,
    tableRef,
}: IBimWorkOrderTotalsTable<T>) => {
    const { columns, bimTotalsTableRowClassRules } = useBimWorkOrderTotalCommonColumns<T>({
        workOrderStatus,
        hasInProgress,
        isFrameContract,
    });

    const onGridReady = useCallback((event: GridReadyEvent) => {
        event.api.sizeColumnsToFit();
    }, []);

    return (
        <div className="ag-theme-alpine BimWorkOrderPrice customHeaderColor">
            <AgGridTable
                rowData={tableData}
                columnDefs={tableColumns ?? columns}
                animateRows
                overlayNoRowsTemplate="Нет данных"
                loadingOverlayComponent={tableLoadingComponent}
                defaultColDef={defaultColDef}
                ref={tableRef}
                rowClassRules={rowClassRules ?? bimTotalsTableRowClassRules}
                suppressDragLeaveHidesColumns
                onGridReady={onGridReady}
            />
        </div>
    );
};

BimWorkOrderTotalsTable.displayName = 'BimWorkOrderTotalsTable';
